export type MarketingModule = {
  title: string;
  slug: string;
  description: string;
  eyebrow: string;
  icon: string;
  highlights: string[];
  stats: [string, string][];
};
export type MarketingSolution = MarketingModule;
export type MarketingIndustry = MarketingModule;
export const platformModules: MarketingModule[] = [
  {
    title: 'Employee Management',
    slug: 'employee-management',
    description:
      'Employee management software for profiles, company structure, employment details, documents and controlled employee self-service in Mentrox OneHR.',
    eyebrow: 'Mentrox OneHR',
    icon: 'Users',
    highlights: [
      'Employee profiles',
      'Organization assignments',
      'Documents and access',
      'Connected payroll inputs',
    ],
    stats: [
      ['Create the record', 'Workflow review'],
      ['Place the employee', 'Workflow review'],
      ['Enable everyday work', 'Workflow review'],
    ],
  },
  {
    title: 'Workforce Management',
    slug: 'workforce-management',
    description:
      'Workforce management software connecting employee assignments, shifts, attendance, leave and operational workspaces in Mentrox OneHR.',
    eyebrow: 'Mentrox OneHR',
    icon: 'BriefcaseBusiness',
    highlights: [
      'Organization context',
      'Shift assignment',
      'Attendance and leave',
      'Operational visibility',
    ],
    stats: [
      ['Organize the workforce', 'Workflow review'],
      ['Review availability', 'Workflow review'],
      ['Coordinate responsibilities', 'Workflow review'],
    ],
  },
  {
    title: 'Attendance Management',
    slug: 'attendance',
    description:
      'Attendance management software with employee punches, shifts, work-hour rules, regularization and payroll inputs for office and shift-based teams.',
    eyebrow: 'Mentrox OneHR',
    icon: 'ClipboardCheck',
    highlights: [
      'Daily attendance records',
      'Shift and work-hour rules',
      'Regularization requests',
      'Location-aware settings',
    ],
    stats: [
      ['Configure the rules', 'Workflow review'],
      ['Capture and review', 'Workflow review'],
      ['Close the period', 'Workflow review'],
    ],
  },
  {
    title: 'Leave Management',
    slug: 'leave',
    description:
      'Leave management software for leave types, balances, employee requests, approvals and company holiday calendars in Mentrox OneHR.',
    eyebrow: 'Mentrox OneHR',
    icon: 'CalendarDays',
    highlights: [
      'Leave types and policies',
      'Employee requests',
      'Balances and decisions',
      'Holiday context',
    ],
    stats: [
      ['Set the policy', 'Workflow review'],
      ['Submit and decide', 'Workflow review'],
      ['Review the period', 'Workflow review'],
    ],
  },
  {
    title: 'Payroll Management',
    slug: 'payroll',
    description:
      'Payroll management software for salary structures, attendance inputs, Indian statutory deductions, reimbursements and company-branded PDF payslips.',
    eyebrow: 'Mentrox OneHR',
    icon: 'WalletCards',
    highlights: [
      'Salary structures and history',
      'Attendance and loss of pay',
      'Statutory configuration',
      'Payslips and reimbursements',
    ],
    stats: [
      ['Review the period', 'Workflow review'],
      ['Process and verify', 'Workflow review'],
      ['Publish the record', 'Workflow review'],
    ],
  },
  {
    title: 'Performance Management',
    slug: 'performance',
    description:
      'Employee performance management software with goals, progress updates, review cycles, configurable forms and employee and manager review stages.',
    eyebrow: 'Mentrox OneHR',
    icon: 'Target',
    highlights: [
      'Goals and progress',
      'Review cycles',
      'Configurable review fields',
      'Employee and manager stages',
    ],
    stats: [
      ['Define the cycle', 'Workflow review'],
      ['Track progress', 'Workflow review'],
      ['Complete the review', 'Workflow review'],
    ],
  },
  {
    title: 'Recruitment & Onboarding',
    slug: 'recruitment',
    description:
      'Recruitment and employee onboarding software for jobs, candidates, interviews, offers and company employee setup in Mentrox OneHR.',
    eyebrow: 'Mentrox OneHR',
    icon: 'UserPlus',
    highlights: ['Jobs and candidates', 'Interview records', 'Offer workflow', 'Employee setup'],
    stats: [
      ['Record the opportunity', 'Workflow review'],
      ['Review candidates', 'Workflow review'],
      ['Prepare joining', 'Workflow review'],
    ],
  },
  {
    title: 'Documents',
    slug: 'documents',
    description:
      'Employee document management software with employee-linked records, document visibility, verification status and expiry information in Mentrox OneHR.',
    eyebrow: 'Mentrox OneHR',
    icon: 'FileText',
    highlights: [
      'Employee-linked records',
      'Visibility settings',
      'Verification status',
      'Expiry information',
    ],
    stats: [
      ['Decide what is needed', 'Workflow review'],
      ['Link and review', 'Workflow review'],
      ['Maintain the record', 'Workflow review'],
    ],
  },
  {
    title: 'Analytics',
    slug: 'analytics',
    description:
      'HR analytics software with workforce dashboards, attendance and payroll reports, and company-scoped operational views in Mentrox OneHR.',
    eyebrow: 'Mentrox OneHR',
    icon: 'BarChart3',
    highlights: [
      'Company dashboards',
      'Attendance reports',
      'Payroll reporting',
      'Workforce intelligence views',
    ],
    stats: [
      ['Set the review question', 'Workflow review'],
      ['Review the records', 'Workflow review'],
      ['Act with context', 'Workflow review'],
    ],
  },
  {
    title: 'AI Assistant',
    slug: 'ai',
    description:
      'An AI HR assistant for supported employee questions, leave information, attendance, payslips and confirmed HR actions in Mentrox OneHR.',
    eyebrow: 'Mentrox OneHR',
    icon: 'Sparkles',
    highlights: [
      'Employee information lookup',
      'Leave workflow assistance',
      'Assigned asset issue reporting',
      'Conversation history',
    ],
    stats: [
      ['Ask a supported question', 'Workflow review'],
      ['Review the response', 'Workflow review'],
      ['Confirm supported actions', 'Workflow review'],
    ],
  },
  {
    title: 'Organization Management',
    slug: 'organization',
    description:
      'HR organization management software for branches, departments, designations, teams, reporting relationships and employee assignments.',
    eyebrow: 'Mentrox OneHR',
    icon: 'Building2',
    highlights: [
      'Branches',
      'Departments and designations',
      'Teams and managers',
      'Employee assignments',
    ],
    stats: [
      ['Create the structure', 'Workflow review'],
      ['Assign employees', 'Workflow review'],
      ['Maintain changes', 'Workflow review'],
    ],
  },
  {
    title: 'Projects',
    slug: 'projects',
    description:
      'Project management software for company projects, assigned tasks, priorities, delivery status and workforce context in Mentrox OneHR.',
    eyebrow: 'Mentrox OneHR',
    icon: 'FolderKanban',
    highlights: [
      'Project records',
      'Task assignment',
      'Progress and activity',
      'Employee workspaces',
    ],
    stats: [
      ['Set up the project', 'Workflow review'],
      ['Break down the work', 'Workflow review'],
      ['Review delivery', 'Workflow review'],
    ],
  },
  {
    title: 'Production',
    slug: 'production',
    description:
      'Explore production management requirements with Mentrox OneHR: available shift, attendance, task and inventory workflows, with dedicated production features planned.',
    eyebrow: 'Supporting workflows',
    icon: 'Factory',
    highlights: [
      'Shift-based workforce records',
      'Operational task ownership',
      'Inventory records',
      'Production requirements review',
    ],
    stats: [
      ['Map the workforce', 'Workflow review'],
      ['Use available records', 'Workflow review'],
      ['Assess the production gap', 'Workflow review'],
    ],
  },
  {
    title: 'Inventory',
    slug: 'inventory',
    description:
      'Inventory management software with product records, warehouses, stock visibility, movements and adjustments in the Mentrox OneHR finance workspace.',
    eyebrow: 'Mentrox OneHR',
    icon: 'Package',
    highlights: [
      'Product records',
      'Warehouses and stock',
      'Stock movements',
      'Adjustments and dashboard',
    ],
    stats: [
      ['Define products and storage', 'Workflow review'],
      ['Record movements', 'Workflow review'],
      ['Review differences', 'Workflow review'],
    ],
  },
];
export const solutions: MarketingSolution[] = [
  {
    title: 'Project Planning',
    slug: 'project-planning',
    description:
      'Project planning software connecting company projects, task ownership, priorities and progress with employee context.',
    eyebrow: 'Mentrox OneHR',
    icon: 'Layers3',
    highlights: [
      'Project records',
      'Task assignment',
      'Progress and activity',
      'Employee workspaces',
    ],
    stats: [
      ['Set up the project', 'Workflow review'],
      ['Break down the work', 'Workflow review'],
      ['Review delivery', 'Workflow review'],
    ],
  },
  {
    title: 'Production Management',
    slug: 'production-management',
    description:
      'Evaluate production planning and management requirements with available workforce, attendance, project/task and inventory workflows.',
    eyebrow: 'Supporting workflows',
    icon: 'Layers3',
    highlights: [
      'Shift-based workforce records',
      'Operational task ownership',
      'Inventory records',
      'Production requirements review',
    ],
    stats: [
      ['Map the workforce', 'Workflow review'],
      ['Use available records', 'Workflow review'],
      ['Assess the production gap', 'Workflow review'],
    ],
  },
  {
    title: 'Breakdown Management',
    slug: 'breakdown-management',
    description:
      'Equipment issue reporting and follow-up with existing asset issue and task workflows; dedicated breakdown dispatch and maintenance planning are not available.',
    eyebrow: 'Mentrox OneHR',
    icon: 'Layers3',
    highlights: ['Assigned asset issues', 'Confirmed assistant reporting', 'Task-based follow-up'],
    stats: [
      ['Identify the asset', 'Workflow review'],
      ['Submit the issue', 'Workflow review'],
      ['Arrange follow-up', 'Workflow review'],
    ],
  },
  {
    title: 'Inventory Management',
    slug: 'inventory-management',
    description:
      'Stock and inventory management workflows for product records, warehouses, movements, adjustments and finance inventory visibility.',
    eyebrow: 'Mentrox OneHR',
    icon: 'Layers3',
    highlights: [
      'Product records',
      'Warehouses and stock',
      'Stock movements',
      'Adjustments and dashboard',
    ],
    stats: [
      ['Define products and storage', 'Workflow review'],
      ['Record movements', 'Workflow review'],
      ['Review differences', 'Workflow review'],
    ],
  },
  {
    title: 'Workforce Operations',
    slug: 'workforce-operations',
    description:
      'Workforce management workflows connecting employee organization, shifts, attendance, leave and project responsibilities.',
    eyebrow: 'Mentrox OneHR',
    icon: 'Layers3',
    highlights: [
      'Organization context',
      'Shift assignment',
      'Attendance and leave',
      'Operational visibility',
    ],
    stats: [
      ['Organize the workforce', 'Workflow review'],
      ['Review availability', 'Workflow review'],
      ['Coordinate responsibilities', 'Workflow review'],
    ],
  },
  {
    title: 'Payroll & Compliance',
    slug: 'payroll-compliance',
    description:
      'Payroll and compliance software in India supporting configured statutory deductions, attendance inputs, payroll history and PDF payslips.',
    eyebrow: 'Mentrox OneHR',
    icon: 'Layers3',
    highlights: [
      'Salary structures and history',
      'Attendance and loss of pay',
      'Statutory configuration',
      'Payslips and reimbursements',
    ],
    stats: [
      ['Review the period', 'Workflow review'],
      ['Process and verify', 'Workflow review'],
      ['Publish the record', 'Workflow review'],
    ],
  },
  {
    title: 'Performance Management',
    slug: 'performance-management',
    description:
      'An employee performance management system connecting goals, progress, review cycles and employee/manager contributions.',
    eyebrow: 'Mentrox OneHR',
    icon: 'Layers3',
    highlights: [
      'Goals and progress',
      'Review cycles',
      'Configurable review fields',
      'Employee and manager stages',
    ],
    stats: [
      ['Define the cycle', 'Workflow review'],
      ['Track progress', 'Workflow review'],
      ['Complete the review', 'Workflow review'],
    ],
  },
];
export const industries: MarketingIndustry[] = [
  {
    title: 'Manufacturing',
    slug: 'manufacturing',
    description:
      'HRMS for manufacturing teams: employee records, assigned shifts, attendance, payroll and available task/inventory workflows.',
    eyebrow: 'Supporting workflows',
    icon: 'Layers3',
    highlights: ['Attendance', 'Payroll', 'Inventory', 'Production'],
    stats: [
      ['Set factory structure', 'Workflow review'],
      ['Review daily exceptions', 'Workflow review'],
      ['Close the period', 'Workflow review'],
    ],
  },
  {
    title: 'Garments & Textiles',
    slug: 'garments',
    description:
      'HRMS for garments and textile businesses with employee organization, shifts, leave, attendance and payroll administration.',
    eyebrow: 'Mentrox OneHR',
    icon: 'Layers3',
    highlights: ['Employee Management', 'Attendance', 'Leave', 'Payroll'],
    stats: [
      ['Organize departments', 'Workflow review'],
      ['Capture and review time', 'Workflow review'],
      ['Review employee totals', 'Workflow review'],
    ],
  },
  {
    title: 'IT & Technology',
    slug: 'technology',
    description:
      'HRMS for IT and technology teams connecting employee onboarding, project tasks, leave, performance cycles and payroll.',
    eyebrow: 'Mentrox OneHR',
    icon: 'Layers3',
    highlights: ['Recruitment & Onboarding', 'Projects', 'Performance', 'Leave'],
    stats: [
      ['Prepare joining', 'Workflow review'],
      ['Assign work', 'Workflow review'],
      ['Review development', 'Workflow review'],
    ],
  },
  {
    title: 'Construction',
    slug: 'construction',
    description:
      'HRMS for construction businesses with branch and employee assignments, attendance, project tasks, assets and payroll records.',
    eyebrow: 'Mentrox OneHR',
    icon: 'Layers3',
    highlights: ['Workforce Management', 'Attendance', 'Projects', 'Payroll'],
    stats: [
      ['Map the sites', 'Workflow review'],
      ['Review workforce records', 'Workflow review'],
      ['Coordinate and close', 'Workflow review'],
    ],
  },
  {
    title: 'Healthcare',
    slug: 'healthcare',
    description:
      'HRMS for healthcare staff administration with employee records, shifts, leave, attendance, documents and payroll workflows.',
    eyebrow: 'Mentrox OneHR',
    icon: 'Layers3',
    highlights: ['Leave', 'Attendance', 'Documents', 'Payroll'],
    stats: [
      ['Set staff assignments', 'Workflow review'],
      ['Review time and documents', 'Workflow review'],
      ['Finalize payroll inputs', 'Workflow review'],
    ],
  },
  {
    title: 'Retail',
    slug: 'retail',
    description:
      'HRMS for retail teams with branch employee records, shifts, attendance, payroll and enabled finance inventory workflows.',
    eyebrow: 'Mentrox OneHR',
    icon: 'Layers3',
    highlights: ['Organization Management', 'Attendance', 'Inventory', 'Payroll'],
    stats: [
      ['Define branches and products', 'Workflow review'],
      ['Review daily administration', 'Workflow review'],
      ['Close HR and finance reviews', 'Workflow review'],
    ],
  },
  {
    title: 'Professional Services',
    slug: 'services',
    description:
      'HRMS for professional services connecting employee records, engagement tasks, time off, performance and payroll administration.',
    eyebrow: 'Mentrox OneHR',
    icon: 'Layers3',
    highlights: ['Projects', 'Performance', 'Leave', 'Employee Management'],
    stats: [
      ['Set up the team', 'Workflow review'],
      ['Coordinate engagements', 'Workflow review'],
      ['Review people processes', 'Workflow review'],
    ],
  },
];
